import { useContext, createContext, ReactNode, useState } from 'react';
import { ButtonProps } from 'neon-design-system';

interface DialogOptions {
  title: string;
  content: ReactNode;
  okText?: string;
  cancelText?: string;
  okColorType?: ButtonProps['colorType'];
  suppressHideOnClose?: boolean;
}

interface DialogContextParams {
  showDialog(options: DialogOptions): Promise<boolean>;
  close(response: boolean): unknown;
  isClosing: boolean;
  isOpen: boolean;
  setIsOpen(isOpen: boolean): unknown;
  dialogOptions?: DialogOptions;
}

const DialogContext = createContext<DialogContextParams | undefined>(undefined);

export const useDialog = () => {
  const context = useContext(DialogContext);
  if (!context) {
    throw new Error('Dialog must be used within a DialogProvider');
  }
  return context;
};

export const DialogProvider = (props: { children: ReactNode }) => {
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [isClosing, setIsClosing] = useState<boolean>(false);
  const [resolve, setResolve] = useState<(value: any) => unknown>();
  const [dialogOptions, setDialogOptions] = useState<DialogOptions | undefined>(undefined);

  const showDialog = (options: DialogOptions) => {
    setIsClosing(false);
    setIsOpen(true);
    setDialogOptions(options);
    return new Promise<boolean>((res) => setResolve(() => res));
  };

  const close = (response: boolean) => {
    resolve?.(response);
    setIsClosing(true);
    if (!dialogOptions?.suppressHideOnClose) {
      setIsOpen(false);
    }
  };

  return <DialogContext.Provider value={{ showDialog, close, isOpen, setIsOpen, isClosing, dialogOptions }}>{props.children}</DialogContext.Provider>;
};
